import { useExpertFeatures } from "@/hooks/useExpertFeatures";
import { planAtLeast } from "@/lib/plans";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Camera, Gem, Images, Sparkles, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { useAuth } from "@/hooks/useAuth";
import { PHOTO_SCAN_MAX_IMAGES } from "@/lib/photo-scan";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/i18n";

/** Filmremsa som löper snett över bakgrunden i Pro-rutan. */
function FilmStrip() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -right-6 -top-10 h-[170%] w-56 -rotate-12 opacity-60 sm:-right-10 sm:w-80 sm:opacity-70"
    >
      <div className="flex h-full w-full flex-col gap-2 rounded-md bg-white/20 p-2 ring-1 ring-white/30 sm:gap-2.5 sm:p-2.5">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex flex-1 items-center gap-2 sm:gap-2.5">
            <span className="h-3 w-3 shrink-0 rounded-[3px] bg-white/70 sm:h-3.5 sm:w-3.5" />
            <span className="h-full flex-1 rounded-sm bg-white/25 ring-1 ring-white/25" />
            <span className="h-3 w-3 shrink-0 rounded-[3px] bg-white/70 sm:h-3.5 sm:w-3.5" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Rutnät av fotorutor som bakgrund i Plus-rutan. */
function PhotoGrid() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -right-4 -top-6 grid w-52 -rotate-6 grid-cols-3 gap-2 opacity-50 sm:-right-8 sm:w-72 sm:gap-2.5 sm:opacity-60"
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <div
          key={i}
          className="aspect-square rounded-md bg-white/25 ring-1 ring-white/30"
          style={{ opacity: 0.55 + ((i * 7) % 5) / 10 }}
        />
      ))}
    </div>
  );
}

/**
 * Nyheten i Pro: filma ett rum och få en lista på allt av värde vi hittar.
 */
export function VideoScanPromo({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: planStatus } = useQuery({
    queryKey: ["plan-status"],
    queryFn: () => getMyPlanStatus(),
    enabled: !!user,
  });
  const isPro = planAtLeast(planStatus?.plan, "pro");

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-primary/40 bg-gradient-to-br from-ocean-900 to-ocean-700 p-4 text-white shadow-sm sm:p-6",
        className,
      )}
    >
      <FilmStrip />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ocean-900 via-ocean-900/85 to-transparent"
      />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide sm:px-2.5 sm:py-1 sm:text-xs">
          <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> {t("promo.newInPro")}
        </span>
        <h2 className="mt-2 max-w-[15ch] font-heading text-xl font-bold leading-tight sm:mt-3 sm:max-w-none sm:text-3xl">
          {t("promo.videoTitle")}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/85">
          {t("promo.videoText")}
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-white/85 sm:mt-4">
          <li className="flex items-center gap-2">
            <Video className="h-4 w-4 shrink-0" /> {t("promo.videoBullet1")}
          </li>
          <li className="flex items-center gap-2">
            <Camera className="h-4 w-4 shrink-0" /> {t("promo.videoBullet2")}
          </li>
        </ul>
        <Button
          asChild
          variant="secondary"
          className="mt-4 w-full gap-2 font-semibold sm:mt-5 sm:w-auto"
        >
          {isPro ? (
            <Link to="/mina-prylar">
              {t("promo.videoCta")} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <Link to="/priser">
              {t("promo.getPro")} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Nyheten i Plus: fota flera prylar på en gång och få dem grupperade.
 */
export function PhotoScanPromo({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: planStatus } = useQuery({
    queryKey: ["plan-status"],
    queryFn: () => getMyPlanStatus(),
    enabled: !!user,
  });
  const hasPhotoScan = planAtLeast(planStatus?.plan, "plus");

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-accent/40 bg-gradient-to-br from-accent/90 to-primary/70 p-4 text-white shadow-sm sm:p-6",
        className,
      )}
    >
      <PhotoGrid />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ocean-900/80 via-ocean-900/60 to-transparent"
      />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide sm:px-2.5 sm:py-1 sm:text-xs">
          <Images className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> {t("promo.newInPlus")}
        </span>
        <h2 className="mt-2 max-w-[16ch] font-heading text-xl font-bold leading-tight sm:mt-3 sm:max-w-none sm:text-3xl">
          {t("promo.photoTitle")}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/90">
          {t("promo.photoText", { max: PHOTO_SCAN_MAX_IMAGES })}
        </p>
        <Button
          asChild
          variant="secondary"
          className="mt-4 w-full gap-2 font-semibold sm:mt-5 sm:w-auto"
        >
          {hasPhotoScan ? (
            <Link to="/mina-prylar">
              {t("promo.photoCta")} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <Link to="/priser">
              {t("promo.getPlus")} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </Button>
      </div>
    </div>
  );
}

/** Expertens djupskanning: en enskild pryl granskas på riktigt. */
export function DeepScanPromo({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { enabled: expertEnabled, testOnly: expertTestOnly } = useExpertFeatures();
  const { data: planStatus } = useQuery({
    queryKey: ["plan-status"],
    queryFn: () => getMyPlanStatus(),
    enabled: !!user,
  });
  const hasDeepScan = planAtLeast(planStatus?.plan, "expert");

  // Expertläget styrs av superadmin och får aldrig synas när det är avstängt.
  if (!expertEnabled) return null;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-primary/40 bg-gradient-to-br from-ocean-800 via-ocean-700 to-primary/70 p-4 text-white shadow-sm sm:p-6",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-12 h-56 w-56 rounded-full bg-white/10 blur-2xl"
      />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide sm:px-2.5 sm:py-1 sm:text-xs">
          <Gem className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {expertTestOnly ? "Test · " : ""}
          {t("promo.newInExpert")}
        </span>

        <h2 className="mt-2 max-w-[16ch] font-heading text-xl font-bold leading-tight sm:mt-3 sm:max-w-none sm:text-3xl">
          {t("promo.deepTitle")}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/90">
          {t("promo.deepText")}
        </p>
        <Button
          asChild
          variant="secondary"
          className="mt-4 w-full gap-2 font-semibold sm:mt-5 sm:w-auto"
        >
          {hasDeepScan ? (
            <Link to="/mina-prylar">
              {t("promo.deepCta")} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <Link to="/priser">
              {t("promo.getExpert")} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </Button>
      </div>
    </div>
  );
}
